import * as Sentry from "@sentry/react";
import { PrimaryButton, SecondaryButton } from "@/components/StandardButton";
import { Card } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { SectionHeader, BodyText } from "@/components/ui/typography";

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

export const ErrorFallback = ({ error, resetError }: ErrorFallbackProps) => {
  const isDev = import.meta.env.MODE !== "production";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full p-8 space-y-6 text-center">
        <div className="flex justify-center">
          <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
        </div>

        <div className="space-y-2">
          <SectionHeader>Something went wrong</SectionHeader>
          <BodyText variant="secondary">
            We've been notified and are working on fixing this issue.
          </BodyText>
        </div>

        {isDev && (
          <div className="bg-muted p-4 rounded-2xl text-left">
            <p className="font-mono text-xs text-destructive break-all">
              {error.message}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <PrimaryButton onClick={resetError}>
            Try Again
          </PrimaryButton>
          <SecondaryButton onClick={() => (window.location.href = "/")}>
            Go Home
          </SecondaryButton>
        </div>

        <p className="text-xs text-muted-foreground">
          If this problem persists, please contact support.
        </p>
      </Card>
    </div>
  );
};

export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: (props) => <ErrorFallback error={props.error as Error} resetError={props.resetError} />,
    showDialog: false,
  }
);
